/**
 * PLT-5514 (v1b) — `useConversationEvents` host-side hook.
 *
 * Opens an SSE subscription to `GET /api/chat/conversations/{id}/events` and
 * fires `onActivity` whenever a new assistant message may have been persisted
 * into the conversation out-of-band (the DD "product set ready" message written
 * by the gpt-product-set job, the async fund-build completion — anything
 * published on the backend `conversationEventBus`). The consumer
 * (`UprightChatInner`) reconciles by pulling the conversation and live-appending
 * anything new, so the message appears without a refresh.
 *
 * `onActivity` fires on TWO signals:
 *   - a `message` event over the stream (the live push), and
 *   - every successful (re)connect (`open`) — a catch-up reconcile.
 *
 * The catch-up-on-connect is what makes delivery self-healing: a transient drop
 * could straddle a publish, and because we reconcile on every connect (and the
 * consumer's reconcile is a no-op when nothing is new), a missed live event is
 * recovered on the next connect rather than lost.
 *
 * Reconnect is manual with capped backoff: EventSource does not auto-retry after
 * an HTTP error status, and its post-failure state is inconsistent across
 * browsers, so we always close-and-reconnect ourselves rather than rely on
 * native retry. EventSource also hides the status code, so the client cannot
 * tell a 410 (no conversation row for this id — see
 * `assertCallerOwnsConversation` on the server) from a dropped connection.
 * `reconnectDelayMs` resolves that by regime: until the first successful
 * connect the only plausible failure is the row not existing, so the streak is
 * cut short; after one, the row is known to exist and a failure is transient,
 * so the streak may run long. The backoff resets on every successful connect.
 *
 * The caller is expected to pass a `conversationId` only once the conversation
 * plausibly exists server-side (`useLiveConversationInjection` waits for the
 * thread to have finished a turn); the short first-connect streak then covers
 * just the sub-second gap between a turn's stream ending and the
 * fire-and-forget `persistChatTurn` landing.
 */
import { useEffect, useRef } from 'react';
import { getServerBaseUrl } from '../untidy/server-base-url';

/** Wire shape of an SSE event from `/api/chat/conversations/{id}/events`. */
interface ConversationSseEvent {
  type: 'subscribed' | 'message' | 'timeout';
  conversationId?: string;
  messageIndex?: number;
}

export interface UseConversationEventsOptions {
  /**
   * Active conversation id. When null, the hook is a no-op. Pass an id only
   * once the conversation plausibly exists server-side — the retry budget
   * before the first successful connect is deliberately small.
   */
  conversationId: string | null;
  /**
   * Fired when a new message may be available — on each `message` event AND on
   * every (re)connect. The consumer should reconcile (pull the conversation and
   * append anything not yet shown); it must be safe to call when nothing is new.
   */
  onActivity: () => void;
}

/**
 * Reconnects tolerated before the stream has ever connected. Sized for the
 * persist race only: the conversation row is written fire-and-forget after a
 * turn's stream ends, so a subscription opened the moment the thread goes idle
 * can beat it by a few hundred milliseconds. Three reconnects span ~7 s of
 * backoff (1 s, 2 s, 4 s); anything still failing after that is a conversation
 * that is not going to appear (not ours, deleted, persistence failed), and the
 * injected message still surfaces on reopen. A long budget here is what filled
 * the console with 410s and burned the whole streak during a long first turn
 * (PLT-6545).
 */
const MAX_RECONNECTS_BEFORE_FIRST_CONNECT = 3;

/**
 * Reconnects tolerated once the stream has connected at least once. The row
 * exists, so a failure is a dropped connection (network blip, laptop sleep, the
 * server's 30-minute stream cap closing on us): 12 reconnects span ~135 s of
 * capped backoff, enough to ride out a short outage.
 */
const MAX_RECONNECTS_AFTER_CONNECT = 12;

const MAX_RECONNECT_DELAY_MS = 15_000;

/**
 * Grace period before the initial connect. The caller latches the id the
 * moment the thread goes idle, and the row is written fire-and-forget after
 * the turn's stream ends, so an immediate connect usually loses that race by a
 * few hundred milliseconds and logs a 410 before the first retry succeeds. A
 * second's delay wins it in the common case and costs nothing: injected
 * messages arrive minutes later, and the catch-up reconcile on `open` covers
 * anything published in between.
 */
const INITIAL_CONNECT_DELAY_MS = 1_000;

/**
 * Backoff policy for one uninterrupted failing streak. Returns the delay before
 * the next connect attempt, or `null` when the streak should stop.
 *
 * @param reconnectsSoFar reconnects already made in this streak (0 when the
 *   initial connect has just failed)
 * @param everConnected whether this subscription has connected successfully at
 *   least once — selects the short (row may not exist) or long (transient
 *   drop) budget
 */
export function reconnectDelayMs(reconnectsSoFar: number, everConnected: boolean): number | null {
  const budget = everConnected ? MAX_RECONNECTS_AFTER_CONNECT : MAX_RECONNECTS_BEFORE_FIRST_CONNECT;
  if (reconnectsSoFar >= budget) return null;
  return Math.min(1000 * 2 ** reconnectsSoFar, MAX_RECONNECT_DELAY_MS);
}

export function useConversationEvents(options: UseConversationEventsOptions): void {
  const { conversationId, onActivity } = options;

  // Hold the latest `onActivity` in a ref so the EventSource always invokes the
  // current callback (which closes over fresh state like the thread's
  // `isRunning`) WITHOUT re-subscribing the stream on every render. The effect
  // below intentionally re-opens only on a real `conversationId` change.
  const onActivityRef = useRef(onActivity);
  useEffect(() => {
    onActivityRef.current = onActivity;
  }, [onActivity]);

  useEffect(() => {
    if (conversationId === null) {
      // Nothing to subscribe to (fresh thread, or a conversation not yet known
      // to exist server-side).
      return;
    }
    if (typeof window === 'undefined' || typeof window.EventSource !== 'function') {
      // Test / SSR environments — no-op.
      return;
    }

    // Same base-url helper the chat adapter uses (`upright-chat-adapter.ts`):
    // the chat already goes cross-origin via `getServerBaseUrl()` on the
    // ui-preview distribution, so a relative URL would hit the wrong origin
    // there. `withCredentials` carries the auth cookie on the cross-origin
    // case (and is harmless same-origin).
    const url = `${getServerBaseUrl()}/api/chat/conversations/${encodeURIComponent(conversationId)}/events`;

    let source: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectsSoFar = 0;
    let everConnected = false;
    let closed = false;

    const scheduleReconnect = () => {
      if (closed) return;
      const delay = reconnectDelayMs(reconnectsSoFar, everConnected);
      if (delay === null) return;
      reconnectsSoFar += 1;
      reconnectTimer = setTimeout(connect, delay);
    };

    function connect() {
      if (closed) return;
      try {
        source = new EventSource(url, { withCredentials: true });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[useConversationEvents] EventSource construction failed', e);
        return;
      }
      source.onopen = () => {
        // Connected — the row exists, so later failures are transient; reset
        // the backoff, then reconcile in case a message landed while we were
        // (re)connecting (a drop around the publish, or the persist race).
        reconnectsSoFar = 0;
        everConnected = true;
        onActivityRef.current();
      };
      source.onmessage = (msg: MessageEvent<string>) => {
        let event: ConversationSseEvent;
        try {
          event = JSON.parse(msg.data) as ConversationSseEvent;
        } catch {
          return;
        }
        // Any `message` event means "something changed" — reconcile. (We ignore
        // the carried index; the consumer pulls the canonical conversation and
        // appends whatever is new.) `subscribed` / `timeout` carry no new state.
        if (event.type === 'message') {
          onActivityRef.current();
        }
      };
      source.onerror = () => {
        // Always tear down and reconnect ourselves — EventSource does not retry
        // after an HTTP error status and its post-failure readyState is
        // inconsistent across browsers, so native retry is not dependable.
        // Close-and-reconnect with the capped backoff above is.
        if (closed) return;
        if (source !== null) {
          source.close();
          source = null;
        }
        scheduleReconnect();
      };
    }

    reconnectTimer = setTimeout(connect, INITIAL_CONNECT_DELAY_MS);

    return () => {
      closed = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      if (source !== null) source.close();
    };
    // `onActivity` is intentionally excluded: it's invoked via `onActivityRef`
    // (kept current by the effect above), so the stream isn't torn down every
    // render — we only re-open on a real `conversationId` change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);
}
